import { useEffect, useMemo, useState } from 'react';
import { postTrending } from '../../services/postTrending';
import { getTopSearches } from '../../services/getTopSearches';
import TrendingSkeletonLoader from './trendingSkeletonLoader';
import './trending.sass';

const MAX_VISIBLE_TOPICS = 4;

/** Devuelve una copia mezclada del arreglo (Fisher-Yates), sin mutar el original. */
function shuffle(array) {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/** Normaliza para no repetir el mismo tema con otra escritura ("Balón de Oro" / "balon de oro"). */
function normalizeTopic(text) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/**
 * Primero los temas de trending (el LLM agrupa las búsquedas contadas, corrige la ortografía y
 * descarta lo que no es noticia); si trending falla o no alcanza, completa con top_searches.
 */
export function mergeTopics(trendingTopics, topSearches) {
  const seen = new Set();
  const merged = [];
  const add = (topic, category) => {
    const key = normalizeTopic(topic || '');
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push({ topic, category });
  };

  shuffle(trendingTopics).forEach(({ topic, category }) => add(topic, category));
  if (merged.length < MAX_VISIBLE_TOPICS) {
    topSearches.forEach(({ query }) => add(query, 'Lo más buscado'));
  }
  return merged.slice(0, MAX_VISIBLE_TOPICS);
}

export default function Trending({ onSearch, isSearching = false }) {
  const [topics, setTopics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Ya vienen mezclados y recortados desde mergeTopics.
  const visibleTopics = useMemo(() => topics.slice(0, MAX_VISIBLE_TOPICS), [topics]);

  const handleTopicSelect = (topic) => {
    if (!topic || isSearching || typeof onSearch !== 'function') return;

    onSearch(topic, 'sugerencia');
  };

  const handleTopicKeyDown = (event, topic) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    handleTopicSelect(topic);
  };

  useEffect(() => {
    const controller = new AbortController();

    async function loadTrending() {
      try {
        const trendingResponse = await postTrending(controller.signal);
        if (controller.signal.aborted || trendingResponse.aborted) return;

        const trendingTopics = trendingResponse.statusCode === 200 && Array.isArray(trendingResponse.data?.topics)
          ? trendingResponse.data.topics
          : [];

        // top_searches solo como respaldo: no hace falta pedirlo si trending ya llena los chips.
        let topSearches = [];
        if (trendingTopics.length < MAX_VISIBLE_TOPICS) {
          const topResponse = await getTopSearches(controller.signal);
          if (controller.signal.aborted || topResponse.aborted) return;
          if (topResponse.statusCode === 200 && Array.isArray(topResponse.data?.searches)) {
            topSearches = topResponse.data.searches;
          }
        }

        setTopics(mergeTopics(trendingTopics, topSearches));
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('[Trending] Error cargando tendencias:', error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }

    loadTrending();

    return () => controller.abort();
  }, []);

  if (isLoading) return <TrendingSkeletonLoader />;

  // Sin resultados: renderiza el contenedor vacío para reservar el espacio (altura fija)
  if (topics.length === 0) {
    return (
      <section
        className="trending-search trending-search--empty"
        aria-label="Sin búsquedas en tendencia"
        aria-hidden="true"
      />
    );
  }

  return (
    <section
      className="trending-search trending-search--ready"
      aria-label="Búsquedas en tendencia"
    >
      <div className='trending-title'>
        <h3>Lo más buscado</h3>
      </div>

      <ul className="trending-search__list">
        {visibleTopics.map(({ topic, category }) => (
          <li
            key={topic}
            className="trending-search__item"
            role="button"
            tabIndex={isSearching ? -1 : 0}
            aria-disabled={isSearching}
            onClick={() => handleTopicSelect(topic)}
            onKeyDown={(event) => handleTopicKeyDown(event, topic)}
          >
            <svg
              className="trending-search__icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="10" cy="11" r="5" />
              <path d="m13.5 14.5 4.5 4.5" />
              <path d="M18.5 2.5v4M16.5 4.5h4M17.1 3.1l2.8 2.8M19.9 3.1l-2.8 2.8" />
            </svg>

            <div className="trending-search__topCat">
              {/* <span className="trending-search__category">{category}</span> */}
              <span className="trending-search__topic">{topic}</span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}